/**
 * AIM (Automatic Item Modification) Diet Update hooks.
 *
 * Backs the React equivalent of the WinForms
 * `ResidentDietOrderManager.UpdateDietOrder` / `AIM_Diet_Update`
 * flow — when a patient's diet order changes, the server walks
 * every meal-pattern + adjustment row (legacy + global lineage),
 * consults `tblItem_Prd_AutoModification`, and proposes
 * per-row attribute changes. React renders the proposed
 * Original/Modified pairs in a dialog; the user accepts or
 * rejects each one; rejected log-row PKeys are posted back to
 * Resolve which calls the wrapper's `ReverseMP/AdjUpdate`.
 *
 * NO inline SQL — the backend goes through the shared
 * wrapper class.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { mealPatternKeys } from './mealPatternApi';
import { adjustmentKeys } from './adjustmentApi';

/**
 * Just the 10 diet/consist FKs the AIM engine reads from the
 * diet-flucs row pair. Each may be `null` (empty slot) — the
 * server treats null + 0 identically. Callers can pass a
 * fully-populated `DietFluc` snapshot here too; the extra
 * fields are ignored on the wire by the server's strongly-typed
 * binder.
 */
export interface AimDietFlucsInput {
  fKey_Diet_Type1?: number | null;
  fKey_Diet_Type2?: number | null;
  fKey_Diet_Type3?: number | null;
  fKey_Diet_Type4?: number | null;
  fKey_Diet_Type5?: number | null;
  fKey_Consist1?: number | null;
  fKey_Consist2?: number | null;
  fKey_Consist3?: number | null;
  fKey_Consist4?: number | null;
  fKey_Consist5?: number | null;
}

export type AimDietSource = 'patientSave' | 'dietFlucsGrid' | 'dashboard' | 'ehr';

const SOURCE_TO_ENUM: Record<AimDietSource, number> = {
  patientSave: 0,
  dietFlucsGrid: 1,
  dashboard: 2,
  ehr: 3,
};

export interface AimRowSnapshot {
  item: string | null;
  itemSize: number | null;
  szMeasure: string | null;
  consist: string | null;
  portion: string | null;
  serviceItem: string | null;
  itemType: string | null;
}

export interface AimProposalDto {
  pKey: number;
  kind: 'mealPattern' | 'adjustment';
  pKey_Log_Prd_Meal_Pattern_Original: number;
  pKey_Log_Prd_Meal_Pattern_Modified: number;
  pKey_Log_Prd_Adjustment_Original: number;
  pKey_Log_Prd_Adjustment_Modified: number;
  mealtime: number | null;
  mealtimeCommaString: string | null;
  group: string | null;
  menuCategory: string | null;
  isGlobalLineage: boolean;
  isInactive: boolean;
  original: AimRowSnapshot;
  modified: AimRowSnapshot;
  originalChildren: AimRowSnapshot[];
  modifiedChildren: AimRowSnapshot[];
  /** Pre-computed by the server — keys are PascalCase column names. */
  changedColumns: string[];
}

/**
 * `AimReportContextDto`: what report 109 (AIM Meal Pattern Changes) prints
 * above the changes. Empty strings / null when the server had nothing.
 */
export interface AimReportContext {
  residentFullName?: string | null;
  oldDietOrder?: string | null;
  newDietOrder?: string | null;
  dietOrderEffectiveDate?: string | null;
  unit?: string | null;
  room?: string | null;
  aimRunDateTime?: string | null;
  aimRunBy?: string | null;
}

/**
 * Wire casing: the C# DTO (DietechModels/Models/AimDietDtos.cs) carries
 * Newtonsoft `[JsonProperty("ReportDataXml")]` etc., but the API writes
 * responses with System.Text.Json (Program.cs `AddJsonOptions`,
 * `FirstCharLowerPolicy`; there is no AddNewtonsoftJson), which ignores
 * Newtonsoft attributes. So every key arrives first-char-lower:
 * `reportDataXml`, `reportDataXmlSchema`, `reportContext.aimRunBy`, ...
 */
export interface AimDietRunResponse {
  pKey_Log_Transaction: number;
  proposals: AimProposalDto[];
  hasProposals: boolean;
  /**
   * The engine's change set (DSResults) as XML: report 109's `StrDataXML`.
   * Empty when there is none (and missing from an API older than 2026-10).
   */
  reportDataXml?: string | null;
  /** Its schema (`StrDataXMLSchema`), only when the change set has child rows. */
  reportDataXmlSchema?: string | null;
  /** Report 109's context box for this run. */
  reportContext?: AimReportContext | null;
}

export interface AimDietRunInput {
  facilityId: number;
  patientId: number;
  rowDietFlucsOriginal: AimDietFlucsInput;
  rowDietFlucsModified: AimDietFlucsInput;
  source: AimDietSource;
}

/**
 * Invalidate every cached query the AIM engine could have
 * mutated server-side (patient meal-pattern + child rows,
 * adjustments + child + exceptions + on-menu). Called from
 * BOTH `useAimDietRun.onSuccess` (production tables already
 * mutated by ExecuteAIM regardless of the user's later
 * Apply/Cancel choice) AND `useAimDietResolve.onSuccess`
 * (after the rejected-row rollback). That double-invalidate is
 * intentional: the Run side guarantees the patient profile sees
 * any changes EVEN IF the user closes the dialog without
 * Apply; the Resolve side picks up the rollbacks of the
 * unselected pairs.
 */
function invalidateMpAdj(queryClient: ReturnType<typeof useQueryClient>, facilityId: number, patientId: number) {
  queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
  queryClient.invalidateQueries({ queryKey: mealPatternKeys.children(facilityId, patientId) });
  queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
  queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
  queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
  queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
}

export function useAimDietRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AimDietRunInput): Promise<AimDietRunResponse> => {
      const { data } = await apiClient.post<AimDietRunResponse>(
        API.aimDietRun(input.facilityId, input.patientId),
        {
          FKey_Patient: input.patientId,
          RowDietFlucsOriginal: input.rowDietFlucsOriginal,
          RowDietFlucsModified: input.rowDietFlucsModified,
          Source: SOURCE_TO_ENUM[input.source],
        },
      );
      return data;
    },
    onSuccess: (data, vars) => {
      // ExecuteAIM has ALREADY mutated tblPrd_Meal_Pattern /
      // tblPrd_Adjustment when this resolves — invalidate so
      // the Meal Pattern + Adjustments tabs refetch even if the
      // user dismisses the proposals dialog. Skip when the
      // server returned no proposals (no rows were touched).
      if (data?.hasProposals) {
        invalidateMpAdj(queryClient, vars.facilityId, vars.patientId);
      }
    },
  });
}

export interface AimDietResolveInput {
  facilityId: number;
  patientId: number;
  pKey_Log_Transaction: number;
  rejectedMPModified: number[];
  rejectedMPOriginal: number[];
  rejectedAdjModified: number[];
  rejectedAdjOriginal: number[];
}

export function useAimDietResolve() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AimDietResolveInput): Promise<void> => {
      await apiClient.post(API.aimDietResolve(input.facilityId, input.patientId), {
        FKey_Patient: input.patientId,
        PKey_Log_Transaction: input.pKey_Log_Transaction,
        RejectedMPModified: input.rejectedMPModified,
        RejectedMPOriginal: input.rejectedMPOriginal,
        RejectedAdjModified: input.rejectedAdjModified,
        RejectedAdjOriginal: input.rejectedAdjOriginal,
      });
    },
    onSuccess: (_data, vars) => {
      // ReverseMP/Adj just restored some rows to their
      // pre-AIM state — the patient profile's meal pattern +
      // adjustments view must refetch so rejected rows show
      // their originals (not the AIM-modified state the user
      // declined). Same invalidation set the Run-side uses.
      invalidateMpAdj(queryClient, vars.facilityId, vars.patientId);
    },
  });
}
